import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { MessageProvider } from "./contexts/MessageContext";
import { CompareProvider } from "./contexts/CompareContext";
import { FeatureFlagProvider } from "./contexts/FeatureFlagContext";
import { FeatureGatedRoute } from "./components/common/FeatureGate";
import CookieConsent from "./components/common/CookieConsent";
import SessionManager from "./components/common/SessionManager";
import AccessibilityToggle from "./components/common/AccessibilityToggle";
import CompareBar from "./components/common/CompareBar";
import "./styles/accessibility.css";

// ScrollToTop component - resets scroll position on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// Public pages
import LandingPage from "./pages/VettProLandingPage";
import CompanyProfile from "./pages/CompanyProfile";
import EmployersPage from "./pages/EmployersPage";
import ProfessionalsPage from "./pages/ProfessionalsPage";
import LoginPage from "./pages/auth/LoginPage";
import SignupPage from "./pages/auth/SignupPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";
import VerifyEmailPage from "./pages/auth/VerifyEmailPage";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import ContactPage from "./pages/ContactPage";

// B4/B5: Dual-Consent Token Flow Pages
import ManagerAssessPage from "./pages/manager/ManagerAssessPage";
import HRVerifyPage from "./pages/hr/HRVerifyPage";

// Legal pages
import PrivacyPolicy from "./pages/legal/PrivacyPolicy";
import TermsOfService from "./pages/legal/TermsOfService";
import CookiePolicy from "./pages/legal/CookiePolicy";
import Impressum from "./pages/legal/Impressum";

// Admin pages
import AdminUsers from "./pages/admin/Users";
import AdminContacts from "./pages/admin/Contacts";
import AdminAnalytics from "./pages/admin/Analytics";
import AdminRecordDetail from "./pages/admin/RecordDetail";
import AdminReviews from "./pages/admin/Reviews";
import AdminReferrals from "./pages/admin/Referrals";
import AdminLogs from "./pages/admin/Logs";
import AdminMessages from "./pages/admin/Messages";
import AdminSettings from "./pages/admin/Settings";
import EmailLog from "./pages/admin/EmailLog";
import RecordsManagement from "./pages/admin/RecordsManagement";
import FinancialOperations from "./pages/admin/FinancialOperations";
import ComplianceCenter from "./pages/admin/ComplianceCenter";
import SystemSettings from "./pages/admin/SystemSettingsEnhanced";
import GDPRDataCentre from "./pages/admin/GDPRDataCentre";
import AdminCommandCenter from "./pages/admin/AdminCommandCenter";

// God Mode Banner
import GodModeBanner from "./components/common/GodModeBanner";

// Founder pages
import FounderDashboard from "./pages/founder/FounderDashboard";
import FounderProfile from "./pages/founder/FounderProfile";
import FounderCompany from "./pages/founder/FounderCompany";
import FounderMessages from "./pages/founder/FounderMessages";
import FounderSearch from "./pages/founder/FounderSearch";

// Employee pages
import EmployeeDashboard from "./pages/employee/Dashboard";
import EmployeeProfile from "./pages/employee/Profile";
import EmployeeRecords from "./pages/employee/Records";
import EmployeeRecordDetail from "./pages/employee/RecordDetail";
import EmployeeRecordReview from "./pages/employee/RecordReview";
import EmployeeMessages from "./pages/employee/Messages";
import EmployeeMessageThread from "./pages/employee/MessageThread";
import EmployeeSettings from "./pages/employee/Settings";
import EmployeeFlagThread from "./pages/employee/FlagThread";
import EmployeeReviews from "./pages/employee/Reviews";
import EmployeeReviewDetail from "./pages/employee/ReviewDetail";
import EmployeeRequestReview from "./pages/employee/RequestReview";
import EmployeeOnboarding from "./pages/employee/Onboarding";
import EmployeeAnalytics from "./pages/employee/Analytics";
import EmployeeConnected from "./pages/employee/Connected";
import EmployeeReferences from "./pages/employee/References";
import EmployeeCompanyProfile from "./pages/employee/CompanyProfile";
import EmployeeRecordRequests from "./pages/employee/RecordRequests";
import EmployeeEmailSignature from "./pages/employee/EmailSignature";
import EmployeePeerReviews from "./pages/employee/PeerReviews";
import EmployeeAccessRequests from "./pages/employee/AccessRequests";
import EmployeeShare from "./pages/employee/Share";
import EmployeeCertificates from "./pages/employee/Certificates";
import EmployeeEducation from "./pages/employee/Education";

// Professional Portal (new dark sidebar design)
import ProfessionalDashboard from "./pages/professional/ProfessionalDashboard";
import VettIdPage from "./pages/professional/VettIdPage";
import RequestRecordPage from "./pages/professional/RequestRecordPage";
import MyRecordsPage from "./pages/professional/MyRecordsPage";
import RecordDetailPage from "./pages/professional/RecordDetailPage";
import OnboardingPage from "./pages/professional/OnboardingPage";

// Public pages (no auth required)
import PublicReference from "./pages/PublicReference";
import PublicRecordRequest from "./pages/PublicRecordRequest";
import PublicProfile from "./pages/PublicProfile";
import SharedRecord from "./pages/SharedRecord";
import EducationVerify from "./pages/EducationVerify";

// Common pages (both employer and employee)
import Referrals from "./pages/common/Referrals";
import FeatureNotAvailable from "./pages/common/FeatureNotAvailable";

// Employer pages
import EmployerDashboard from "./pages/employer/Dashboard";
import EmployerProfile from "./pages/employer/Profile";
import EmployerSearch from "./pages/employer/Search";
import EmployerRecords from "./pages/employer/Records";
import EmployerRecordNew from "./pages/employer/RecordNew";
import EmployerRecordDetail from "./pages/employer/RecordDetail";
import EmployerMessages from "./pages/employer/Messages";
import EmployerMessageThread from "./pages/employer/MessageThread";
import EmployerSettings from "./pages/employer/Settings";
import EmployerSavedCandidates from "./pages/employer/SavedCandidates";
import EmployerReviews from "./pages/employer/Reviews";
import EmployerReviewNew from "./pages/employer/ReviewNew";
import EmployerReviewDetail from "./pages/employer/ReviewDetail";
import EmployerReviewCalendar from "./pages/employer/ReviewCalendar";
import EmployerOnboarding from "./pages/employer/Onboarding";
import EmployerAnalytics from "./pages/employer/Analytics";
import EmployerPerformanceAnalytics from "./pages/employer/PerformanceAnalytics";
import EmployerTalentProfile from "./pages/employer/TalentProfile";
import EmployerTeamComparison from "./pages/employer/TeamComparison";
import EmployerCandidateComparison from "./pages/employer/CandidateComparison";
import EmployerCompany from "./pages/employer/Company";
import EmployerStaffDetail from "./pages/employer/StaffDetail";
import EmployerPipeline from "./pages/employer/Pipeline";
import EmployerBilling from "./pages/employer/Billing";
import EmployerATSSettings from "./pages/employer/ATSIntegrationSettings";

// Protected Route Component
const ProtectedRoute = ({ children, allowedType }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedType && user.account_type !== allowedType) {
    return <Navigate to={getDashboardPath(user)} replace />;
  }
  
  return children;
};

// Helper function to get dashboard path based on account type and admin status
const getDashboardPath = (user) => {
  // SUPERADMIN ALWAYS GOES TO FOUNDER DASHBOARD
  if (user?.is_superadmin) {
    return '/founder/dashboard';
  }
  
  // Regular admin goes to command center
  if (user?.is_admin) {
    return '/admin/command-center';
  }
  
  switch (user?.account_type) {
    case 'admin':
      return '/admin/command-center';
    case 'employer':
    case 'agency':
      return '/employer/dashboard';
    case 'employee':
    default:
      return '/employee/dashboard';
  }
};

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={user ? <Navigate to={getDashboardPath(user)} replace /> : <LandingPage />} />
      <Route path="/employers" element={user ? <Navigate to={getDashboardPath(user)} replace /> : <EmployersPage />} />
      <Route path="/professionals" element={user ? <Navigate to={getDashboardPath(user)} replace /> : <ProfessionalsPage />} />
      <Route path="/login" element={user ? <Navigate to={getDashboardPath(user)} replace /> : <LoginPage />} />
      <Route path="/signup" element={user ? <Navigate to={getDashboardPath(user)} replace /> : <SignupPage />} />
      <Route path="/forgot-password" element={user ? <Navigate to={getDashboardPath(user)} replace /> : <ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/company/:slug" element={<CompanyProfile />} />
      
      {/* Legal Pages */}
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/cookies" element={<CookiePolicy />} />
      <Route path="/impressum" element={<Impressum />} />
      
      {/* B4/B5: Dual-Consent Token Flow Routes (Public, No Auth Required) */}
      <Route path="/manager-assess/:token" element={<ManagerAssessPage />} />
      <Route path="/hr-verify/:token" element={<HRVerifyPage />} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={user?.is_admin ? <AdminCommandCenter /> : <Navigate to="/" replace />} />
      <Route path="/admin/dashboard" element={user?.is_admin ? <AdminCommandCenter /> : <Navigate to="/" replace />} />
      <Route path="/admin/command-center" element={user?.is_admin ? <AdminCommandCenter /> : <Navigate to="/" replace />} />
      <Route path="/admin/users" element={user?.is_admin ? <AdminUsers /> : <Navigate to="/" replace />} />
      <Route path="/admin/contacts" element={user?.is_admin ? <AdminContacts /> : <Navigate to="/" replace />} />
      <Route path="/admin/analytics" element={user?.is_admin ? <AdminAnalytics /> : <Navigate to="/" replace />} />
      <Route path="/admin/records" element={user?.is_admin ? <RecordsManagement /> : <Navigate to="/" replace />} />
      <Route path="/admin/records/:id" element={user?.is_admin ? <AdminRecordDetail /> : <Navigate to="/" replace />} />
      <Route path="/admin/reviews" element={user?.is_admin ? <AdminReviews /> : <Navigate to="/" replace />} />
      <Route path="/admin/referrals" element={user?.is_admin ? <AdminReferrals /> : <Navigate to="/" replace />} />
      <Route path="/admin/logs" element={user?.is_admin ? <AdminLogs /> : <Navigate to="/" replace />} />
      <Route path="/admin/email-logs" element={user?.is_admin ? <EmailLog /> : <Navigate to="/" replace />} />
      <Route path="/admin/messages" element={user?.is_admin ? <AdminMessages /> : <Navigate to="/" replace />} />
      <Route path="/admin/settings" element={user?.is_admin ? <AdminSettings /> : <Navigate to="/" replace />} />
      {/* Phase 2: Financial & Analytics */}
      <Route path="/admin/financial" element={user?.is_admin ? <FinancialOperations /> : <Navigate to="/" replace />} />
      {/* Phase 3: Compliance, Settings */}
      <Route path="/admin/compliance" element={user?.is_admin ? <ComplianceCenter /> : <Navigate to="/" replace />} />
      <Route path="/admin/system-settings" element={user?.is_admin ? <SystemSettings /> : <Navigate to="/" replace />} />
      <Route path="/admin/gdpr" element={user?.is_admin ? <GDPRDataCentre /> : <Navigate to="/" replace />} />
      <Route path="/admin/god-mode" element={user?.is_admin ? <AdminCommandCenter /> : <Navigate to="/" replace />} />
      
      {/* Founder Routes - Superadmin only */}
      <Route path="/founder/dashboard" element={user?.is_superadmin ? <FounderDashboard /> : <Navigate to="/" replace />} />
      <Route path="/founder/profile" element={user?.is_superadmin ? <FounderDashboard /> : <Navigate to="/" replace />} />
      <Route path="/founder/company" element={user?.is_superadmin ? <FounderDashboard /> : <Navigate to="/" replace />} />
      <Route path="/founder/messages" element={user?.is_superadmin ? <FounderDashboard /> : <Navigate to="/" replace />} />
      <Route path="/founder/search" element={user?.is_superadmin ? <FounderDashboard /> : <Navigate to="/" replace />} />
      
      {/* Employee Routes */}
      <Route path="/employee/onboarding" element={<ProtectedRoute allowedType="employee"><EmployeeOnboarding /></ProtectedRoute>} />
      <Route path="/employee/dashboard" element={<ProtectedRoute allowedType="employee"><EmployeeDashboard /></ProtectedRoute>} />
      <Route path="/employee/profile" element={<ProtectedRoute allowedType="employee"><EmployeeProfile /></ProtectedRoute>} />
      <Route path="/employee/records" element={<ProtectedRoute allowedType="employee"><EmployeeRecords /></ProtectedRoute>} />
      <Route path="/employee/records/:id" element={<ProtectedRoute allowedType="employee"><EmployeeRecordDetail /></ProtectedRoute>} />
      <Route path="/employee/records/:id/review" element={<ProtectedRoute allowedType="employee"><EmployeeRecordReview /></ProtectedRoute>} />
      <Route path="/employee/reviews" element={<ProtectedRoute allowedType="employee"><EmployeeReviews /></ProtectedRoute>} />
      <Route path="/employee/reviews/:id" element={<ProtectedRoute allowedType="employee"><EmployeeReviewDetail /></ProtectedRoute>} />
      <Route path="/employee/reviews/request" element={<ProtectedRoute allowedType="employee"><EmployeeRequestReview /></ProtectedRoute>} />
      {/* I3: Messaging feature-gated */}
      <Route path="/employee/messages" element={<ProtectedRoute allowedType="employee"><FeatureGatedRoute flag="messaging"><EmployeeMessages /></FeatureGatedRoute></ProtectedRoute>} />
      <Route path="/employee/messages/:id" element={<ProtectedRoute allowedType="employee"><FeatureGatedRoute flag="messaging"><EmployeeMessageThread /></FeatureGatedRoute></ProtectedRoute>} />
      <Route path="/employee/settings" element={<ProtectedRoute allowedType="employee"><EmployeeSettings /></ProtectedRoute>} />
      <Route path="/employee/flags/:recordId/:flagId" element={<ProtectedRoute allowedType="employee"><EmployeeFlagThread /></ProtectedRoute>} />
      <Route path="/employee/analytics" element={<ProtectedRoute allowedType="employee"><EmployeeAnalytics /></ProtectedRoute>} />
      <Route path="/employee/connected" element={<ProtectedRoute allowedType="employee"><EmployeeConnected /></ProtectedRoute>} />
      <Route path="/employee/references" element={<ProtectedRoute allowedType="employee"><EmployeeReferences /></ProtectedRoute>} />
      <Route path="/employee/record-requests" element={<ProtectedRoute allowedType="employee"><EmployeeRecordRequests /></ProtectedRoute>} />
      <Route path="/employee/email-signature" element={<ProtectedRoute allowedType="employee"><EmployeeEmailSignature /></ProtectedRoute>} />
      <Route path="/employee/company/:employerId" element={<ProtectedRoute allowedType="employee"><EmployeeCompanyProfile /></ProtectedRoute>} />
      {/* I3: Referral program feature-gated */}
      <Route path="/employee/referrals" element={<ProtectedRoute allowedType="employee"><FeatureGatedRoute flag="referral_program"><Referrals /></FeatureGatedRoute></ProtectedRoute>} />
      {/* I3: Peer reviews feature-gated */}
      <Route path="/employee/peer-reviews" element={<ProtectedRoute allowedType="employee"><FeatureGatedRoute flag="peer_reviews"><EmployeePeerReviews /></FeatureGatedRoute></ProtectedRoute>} />
      <Route path="/employee/access-requests" element={<ProtectedRoute allowedType="employee"><EmployeeAccessRequests /></ProtectedRoute>} />
      <Route path="/employee/share" element={<ProtectedRoute allowedType="employee"><EmployeeShare /></ProtectedRoute>} />
      <Route path="/employee/certificates" element={<ProtectedRoute allowedType="employee"><EmployeeCertificates /></ProtectedRoute>} />
      <Route path="/employee/education" element={<ProtectedRoute allowedType="employee"><EmployeeEducation /></ProtectedRoute>} />
      
      {/* Public pages (no auth required) */}
      <Route path="/reference/:token" element={<PublicReference />} />
      <Route path="/record-request/:token" element={<PublicRecordRequest />} />
      <Route path="/id/:vettId" element={<PublicProfile />} />
      <Route path="/record/:token" element={<SharedRecord />} />
      <Route path="/education-verify/:token" element={<EducationVerify />} />
      
      {/* Professional Portal Routes (new dark sidebar design) */}
      <Route path="/professional/dashboard" element={<ProtectedRoute allowedType="employee"><ProfessionalDashboard /></ProtectedRoute>} />
      <Route path="/professional/vett-id" element={<ProtectedRoute allowedType="employee"><VettIdPage /></ProtectedRoute>} />
      <Route path="/professional/records" element={<ProtectedRoute allowedType="employee"><MyRecordsPage /></ProtectedRoute>} />
      <Route path="/professional/records/:id" element={<ProtectedRoute allowedType="employee"><RecordDetailPage /></ProtectedRoute>} />
      <Route path="/professional/request-record" element={<ProtectedRoute allowedType="employee"><RequestRecordPage /></ProtectedRoute>} />
      <Route path="/professional/verification-requests" element={<ProtectedRoute allowedType="employee"><ProfessionalDashboard /></ProtectedRoute>} />
      <Route path="/professional/billing" element={<ProtectedRoute allowedType="employee"><ProfessionalDashboard /></ProtectedRoute>} />
      <Route path="/professional/settings" element={<ProtectedRoute allowedType="employee"><ProfessionalDashboard /></ProtectedRoute>} />
      <Route path="/professional/notifications" element={<ProtectedRoute allowedType="employee"><ProfessionalDashboard /></ProtectedRoute>} />
      <Route path="/professional/support" element={<ProtectedRoute allowedType="employee"><ProfessionalDashboard /></ProtectedRoute>} />
      <Route path="/professional/onboarding" element={<ProtectedRoute allowedType="employee"><OnboardingPage /></ProtectedRoute>} />
      
      {/* Employer Routes */}
      <Route path="/employer/onboarding" element={<ProtectedRoute allowedType="employer"><EmployerOnboarding /></ProtectedRoute>} />
      <Route path="/employer/dashboard" element={<ProtectedRoute allowedType="employer"><EmployerDashboard /></ProtectedRoute>} />
      <Route path="/employer/profile" element={<ProtectedRoute allowedType="employer"><EmployerProfile /></ProtectedRoute>} />
      <Route path="/employer/search" element={<ProtectedRoute allowedType="employer"><EmployerSearch /></ProtectedRoute>} />
      <Route path="/employer/records" element={<ProtectedRoute allowedType="employer"><EmployerRecords /></ProtectedRoute>} />
      <Route path="/employer/records/new" element={<ProtectedRoute allowedType="employer"><EmployerRecordNew /></ProtectedRoute>} />
      <Route path="/employer/records/:id/edit" element={<ProtectedRoute allowedType="employer"><EmployerRecordNew /></ProtectedRoute>} />
      <Route path="/employer/records/:id" element={<ProtectedRoute allowedType="employer"><EmployerRecordDetail /></ProtectedRoute>} />
      <Route path="/employer/reviews" element={<ProtectedRoute allowedType="employer"><EmployerReviews /></ProtectedRoute>} />
      <Route path="/employer/reviews/calendar" element={<ProtectedRoute allowedType="employer"><EmployerReviewCalendar /></ProtectedRoute>} />
      <Route path="/employer/reviews/new" element={<ProtectedRoute allowedType="employer"><EmployerReviewNew /></ProtectedRoute>} />
      <Route path="/employer/reviews/:id" element={<ProtectedRoute allowedType="employer"><EmployerReviewDetail /></ProtectedRoute>} />
      {/* I3: Messaging feature-gated */}
      <Route path="/employer/messages" element={<ProtectedRoute allowedType="employer"><FeatureGatedRoute flag="messaging"><EmployerMessages /></FeatureGatedRoute></ProtectedRoute>} />
      <Route path="/employer/messages/:id" element={<ProtectedRoute allowedType="employer"><FeatureGatedRoute flag="messaging"><EmployerMessageThread /></FeatureGatedRoute></ProtectedRoute>} />
      <Route path="/employer/settings" element={<ProtectedRoute allowedType="employer"><EmployerSettings /></ProtectedRoute>} />
      {/* I3: ATS Integration feature-gated */}
      <Route path="/employer/settings/ats" element={<ProtectedRoute allowedType="employer"><FeatureGatedRoute flag="ats_integration"><EmployerATSSettings /></FeatureGatedRoute></ProtectedRoute>} />
      <Route path="/employer/saved" element={<ProtectedRoute allowedType="employer"><EmployerSavedCandidates /></ProtectedRoute>} />
      <Route path="/employer/analytics" element={<ProtectedRoute allowedType="employer"><EmployerAnalytics /></ProtectedRoute>} />
      <Route path="/employer/analytics/performance" element={<ProtectedRoute allowedType="employer"><EmployerPerformanceAnalytics /></ProtectedRoute>} />
      {/* I3: Team Comparison feature-gated */}
      <Route path="/employer/team-comparison" element={<ProtectedRoute allowedType="employer"><FeatureGatedRoute flag="team_comparison"><EmployerTeamComparison /></FeatureGatedRoute></ProtectedRoute>} />
      <Route path="/employer/compare" element={<ProtectedRoute allowedType="employer"><EmployerCandidateComparison /></ProtectedRoute>} />
      <Route path="/employer/talent/:employeeId" element={<ProtectedRoute allowedType="employer"><EmployerTalentProfile /></ProtectedRoute>} />
      <Route path="/employer/company" element={<ProtectedRoute allowedType="employer"><EmployerCompany /></ProtectedRoute>} />
      <Route path="/employer/staff/:employeeId" element={<ProtectedRoute allowedType="employer"><EmployerStaffDetail /></ProtectedRoute>} />
      {/* I3: Pipeline feature-gated */}
      <Route path="/employer/pipeline" element={<ProtectedRoute allowedType="employer"><FeatureGatedRoute flag="pipeline"><EmployerPipeline /></FeatureGatedRoute></ProtectedRoute>} />
      <Route path="/employer/billing" element={<ProtectedRoute allowedType="employer"><EmployerBilling /></ProtectedRoute>} />
      <Route path="/employer/settings/billing" element={<ProtectedRoute allowedType="employer"><EmployerBilling /></ProtectedRoute>} />
      {/* I3: Referral program feature-gated */}
      <Route path="/employer/referrals" element={<ProtectedRoute allowedType="employer"><FeatureGatedRoute flag="referral_program"><Referrals /></FeatureGatedRoute></ProtectedRoute>} />
      
      {/* I3: Feature Not Available - neutral 404-style page */}
      <Route path="/feature-not-available" element={<FeatureNotAvailable />} />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <FeatureFlagProvider>
            <MessageProvider>
              <ScrollToTop />
              <GodModeBanner />
              <SessionManager>
                <CompareProvider>
                  <AppRoutes />
                  <CompareBar />
                </CompareProvider>
              </SessionManager>
              <CookieConsent />
              <AccessibilityToggle />
              <Toaster 
                position="top-right" 
                toastOptions={{
                  style: {
                    background: '#1A1A2E',
                    border: '1px solid #2A2A4A',
                    color: '#F5F5F7',
                  },
                }}
              />
            </MessageProvider>
          </FeatureFlagProvider>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
